import { motion } from "framer-motion";
import { ScrollText, ShieldCheck, Scale, AlertTriangle, Eye, Coins } from "lucide-react";
import { Card } from "@/components/ui/card";

const SECTIONS = [
  {
    icon: ShieldCheck,
    title: "Acceptance of Terms",
    text: "By logging in with Discord and inviting the Areon bot to your Discord server or using the Areon web dashboard, you agree to be bound by these Terms of Service. If you do not agree, you must not use our bot, website, or dashboard services.",
  },
  {
    icon: ScrollText,
    title: "Description of Service",
    text: "Areon is a multi-purpose Discord bot providing community automation, support tickets, suggestions, reaction roles, giveaways, polls, notifications, and economy/gaming features. Some advanced features — including the AI Ticket Pilot, deep moderation logs, and advanced security configurations — may require a paid Premium subscription.",
  },
  {
    icon: Scale,
    title: "User Conduct & Responsibilities",
    text: "You are responsible for all activity occurring in your Discord server under your account. You agree not to use Areon to violate any local laws, harass other users, distribute malicious software, or bypass Discord's official Developer Terms of Service or Community Guidelines. We reserve the right to suspend or block servers violating these rules.",
  },
  {
    icon: AlertTriangle,
    title: "Disclaimer of Warranty & Liability",
    text: "Areon is provided 'as is' and 'as available' without warranties of any kind, either express or implied. We do not guarantee that the bot or dashboard will be uninterrupted, bug-free, or 100% secure. In no event shall the developers of Areon be liable for any data loss, service interruption, or consequential damages.",
  },
  {
    icon: Coins,
    title: "Subscription Billing & Refunds",
    text: "Payments for Areon Premium are processed securely via Lemon Squeezy. You may request a full refund within 5 days of your initial purchase or renewal. Refund requests made after 5 days of active subscription ownership are not eligible. To request a refund, please contact support via Discord.",
  },
  {
    icon: Eye,
    title: "Modifications & Governing Law",
    text: "We reserve the right to modify these terms at any time. Your continued use of Areon after changes are published constitutes acceptance of the revised terms. These terms are governed by standard internet regulations and Discord Platform requirements.",
  },
];

export function Terms() {
  return (
    <div className="mx-auto max-w-3xl py-8 sm:py-12">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl border border-border/50 bg-gradient-to-br from-primary/10 via-card to-card p-6 sm:p-8 mb-8"
      >
        <div className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-primary/20 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-1 text-[11px] font-medium text-primary mb-3">
            <ScrollText className="h-3 w-3" />
            Terms of Service
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">
            Rules of the road
          </h1>
          <p className="mt-2 text-[13px] sm:text-sm text-muted-foreground max-w-xl leading-relaxed">
            Please read these terms carefully before using the Areon Discord bot and dashboard services.
          </p>
          <p className="mt-4 text-[11px] text-muted-foreground/60 font-mono">Last updated: June 23, 2026</p>
        </div>
      </motion.div>

      {/* Intro */}
      <p className="text-sm text-muted-foreground leading-relaxed mb-6 px-1">
        Welcome to Areon. These Terms of Service govern your access to and use of our Discord application, dashboard, and related services.
      </p>

      {/* Sections */}
      <div className="space-y-3">
        {SECTIONS.map((section, i) => {
          const Icon = section.icon;
          return (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.3, delay: i * 0.04 }}
            >
              <Card className="flex gap-4 p-5 border-border/50 bg-card/50 hover:border-primary/20 transition-colors">
                <div className="h-9 w-9 rounded-lg bg-primary/10 ring-1 ring-primary/15 flex items-center justify-center shrink-0 mt-0.5">
                  <Icon className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold mb-1">{section.title}</h3>
                  <p className="text-[13px] text-muted-foreground leading-relaxed">{section.text}</p>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Footer */}
      <p className="mt-8 text-center text-xs text-muted-foreground/60">
        Legal inquiries or support?{" "}
        <a href="https://discord.gg/8eh7KrMPXg" target="_blank" rel="noreferrer" className="text-primary hover:underline">
          Join our Discord server
        </a>.
      </p>
    </div>
  );
}
